import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { ExportTaskFormValues } from "../../ExportTasksForm.types";
import type { TemplateField } from "./TemplateEditor.types";

const FORM_FIELD_BY_TAB: Record<
  TemplateField,
  "titleTemplate" | "descriptionTemplate"
> = {
  title: "titleTemplate",
  description: "descriptionTemplate",
};

interface UseTemplateSwitchResult {
  enabled: boolean;
  formFieldName: "titleTemplate" | "descriptionTemplate";
  turnOn: (onAfter?: () => void) => void;
  turnOff: () => void;
}

// Управляет включением/выключением шаблона: Switch ↔ RHF state синхронизированы
// в одну сторону (RHF → Switch), Switch только вызывает actions. Без этого
// reset формы извне (другая задача) не подтянет Switch к актуальному состоянию.
export const useTemplateSwitch = (
  field: TemplateField,
): UseTemplateSwitchResult => {
  const { control, setValue, unregister } =
    useFormContext<ExportTaskFormValues>();
  const formFieldName = FORM_FIELD_BY_TAB[field];
  const currentValue = useWatch({ control, name: formFieldName });
  const [enabled, setEnabled] = useState(!!currentValue?.template);

  // Синхра только на «появилось» / «совсем исчезло». На пустую строку шаблона
  // не реагируем — иначе только что включённый Switch немедленно погасит сам
  // себя (turnOn → setValue({template:""}) → эффект → setEnabled(false)).
  useEffect(() => {
    if (currentValue === undefined) {
      setEnabled(false);
    } else if (currentValue.template) {
      setEnabled(true);
    }
  }, [currentValue]);

  const turnOn = useCallback(
    (onAfter?: () => void) => {
      setEnabled(true);
      if (!currentValue) {
        setValue(formFieldName, { template: "" }, { shouldDirty: true });
      }
      if (onAfter) requestAnimationFrame(onAfter);
    },
    [currentValue, formFieldName, setValue],
  );

  // unregister, а не setValue(undefined): иначе RHF сохранит ключ с undefined
  // и isDirty в родительской форме залипнет (deepEqual видит лишний ключ).
  const turnOff = useCallback(() => {
    setEnabled(false);
    unregister(formFieldName, { keepDirty: true });
  }, [formFieldName, unregister]);

  return { enabled, formFieldName, turnOn, turnOff };
};
